import omit from 'lodash.omit';

import {
  getAlbumFolderSubscriptionCartItem,
  getAlbumSubscriptionCartItem,
} from '../../../../../../../services/checkout/redux/helpers';
import {
  albumMembershipGroupSupportsLegacyPrintPurchasing,
  getAlbumMembershipGroupAlbumPrintPackages,
  getAlbumMembershipGroupPhotoPricing,
} from '../PhotoPurchasingOptions/helpers';
import { IDENTITY_RELATIONSHIP } from '../../../../../../../constants/identity';
import { getIdentityName } from '../../../../../../../util/identity';
import { CUSTOM_PRINT_TYPE } from '../../../../../../../constants/pro-custom-prints';
import { getAlbumMembershipGroupEntity } from '../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import { isAlbumFolder } from '../../../../../../../util/album-folder';

/**
 * Returns all available AlbumCustomPrints on an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Array} jerseyColors the jersey colors used by the active identity
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupAlbumCustomPrints = (
  albumMembershipGroup,
  jerseyColors = [],
  sellPrintsEnabled = true,
) => {
  if (!sellPrintsEnabled) {
    return [];
  }

  const albumCustomPrints =
    albumMembershipGroup?.album_custom_prints?.records ?? [];

  if (jerseyColors.length > 0) {
    return albumCustomPrints.filter(
      ({ custom_print: customPrint }) =>
        !customPrint?.jersey_colors?.records?.length ||
        !!customPrint?.jersey_colors?.records?.find(({ jersey_color: color }) =>
          jerseyColors.includes(color),
        ),
    );
  }

  return albumCustomPrints;
};

/**
 * Returns all available EventCollages on an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupEventCollages = (
  albumMembershipGroup,
  sellPrintsEnabled = true,
) => {
  if (!sellPrintsEnabled) {
    return [];
  }

  return albumMembershipGroup?.collage_custom_prints?.records ?? [];
};

/**
 * Returns all available AlbumCustomPrints of type ACCOUNT_CARD on an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Array} jerseyColors the jersey colors used by the active identity
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupAccountCardAlbumCustomPrints = (
  albumMembershipGroup,
  jerseyColors = [],
  sellPrintsEnabled = true,
) => {
  if (!sellPrintsEnabled) {
    return [];
  }

  const albumCustomPrints =
    albumMembershipGroup?.account_card_album_custom_prints?.records ?? [];

  if (jerseyColors.length > 0) {
    return albumCustomPrints.filter(
      ({ custom_print: customPrint }) =>
        !customPrint?.jersey_colors?.records?.length ||
        !!customPrint?.jersey_colors?.records?.find(({ jersey_color: color }) =>
          jerseyColors.includes(color),
        ),
    );
  }

  return albumCustomPrints;
};

/**
 * Returns all available AlbumCustomPrints of type CUSTOM_PRINT & subtype SPECIALTY_PRINT on an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupSpecialtyAlbumCustomPrints = (
  albumMembershipGroup,
  sellPrintsEnabled = true,
) => {
  if (!sellPrintsEnabled) {
    return [];
  }

  return albumMembershipGroup?.specialty_custom_prints?.records ?? [];
};

/**
 * Returns all available DigitalBundles on an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {Array}
 */
export const getAlbumMembershipGroupDigitalBundles = albumMembershipGroup => {
  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);
  const albumFolder = isAlbumFolder(entity)
    ? entity
    : entity?.root_album_folder;

  return albumFolder?.digital_bundles?.records ?? [];
};

/**
 * Returns the first discounted AlbumCustomPrint on an AlbumMembershipGroup that is part of the subscription bundle.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {Object|undefined}
 */
export const getAlbumMembershipGroupBundleAlbumCustomPrint = (
  albumMembershipGroup,
  sellPrintsEnabled = true,
) => {
  if (!albumMembershipGroup || !sellPrintsEnabled) {
    return undefined;
  }

  const [discountedAlbumCustomPrint] =
    albumMembershipGroup?.album_custom_prints?.records?.filter(
      albumCustomPrint => albumCustomPrint.discount_price?.value > 0,
    ) ?? [];
  const [albumCustomPrint] =
    albumMembershipGroup?.album_custom_prints?.records ?? [];

  // Fetch the discounted AlbumCustomPrint if it exists, otherwise fetch the first one
  return discountedAlbumCustomPrint || albumCustomPrint;
};

/**
 * Given an album it returns its root subscription album folder.
 *
 * @param {Object} album The Album.
 * @returns {undefined|{AlbumFolder}}
 */
export const getAlbumSubscriptionAlbumFolder = album => {
  if (!album) {
    return undefined;
  }

  return album.root_album_folder?.price?.value > 0
    ? album.root_album_folder
    : undefined;
};

/**
 * Given an AlbumMembershipGroup it returns its subscription AlbumFolder.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {{AlbumFolder}|undefined}
 */
export const getAlbumMembershipGroupSubscriptionAlbumFolder = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return undefined;
  }

  const subscriptionAlbumFolder =
    albumMembershipGroup.album_folder?.price?.value > 0
      ? albumMembershipGroup.album_folder
      : undefined;

  return (
    getAlbumSubscriptionAlbumFolder(albumMembershipGroup.album) ||
    subscriptionAlbumFolder
  );
};

/**
 * Given an AlbumMembershipGroup it returns its subscription Album.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @returns {{Album}|undefined}
 */
export const getAlbumMembershipGroupSubscriptionAlbum = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return undefined;
  }

  return albumMembershipGroup.album?.price_whole_album?.value > 0
    ? albumMembershipGroup.album
    : undefined;
};

/**
 * Given an AlbumMembershipGroup and an Identity ID it returns
 * the corresponding AlbumMembershipGroupIdentity.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Array} albumMembershipGroup.identities The AlbumMembershipGroupIdentities.
 * @param {string} identityId The identity id to look for.
 * @returns {Object}
 */
export const getAlbumMembershipGroupIdentityByIdentityId = (
  albumMembershipGroup,
  identityId,
) => {
  return (albumMembershipGroup?.identities ?? []).find(
    albumMembershipGroupIdentity =>
      albumMembershipGroupIdentity.identity.id === identityId,
  );
};

/**
 * Returns the proper calling name for an identity.
 *
 * @param {Object} identity The identity.
 * @param {string} identity.name The identity's first name.
 * @param {Object} [possessive] If the identity should be addressed in the possessive form (i.e. "Ben's" instead of Ben).
 * @returns {string}
 */
export const getIdentityCallingName = (identity, possessive = true) => {
  if (!possessive) {
    if (identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY) {
      return 'you';
    }

    return `${getIdentityName(identity)}`;
  }

  if (identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY) {
    return 'your';
  }

  return `${getIdentityName(identity)}'s`;
};

/**
 * Determines whether an AlbumCustomPrint is supported to configure
 * before checkout.
 *
 * @param {Object} [albumCustomPrint] The AlbumCustomPrint.
 * @returns {boolean}
 */
export const isAlbumCustomPrintConfigurableInPlace = albumCustomPrint => {
  if (!albumCustomPrint) {
    return false;
  }

  const { description, photos_no: photosNo } =
    albumCustomPrint.custom_print ?? {};
  const isCard =
    albumCustomPrint.custom_print?.type === CUSTOM_PRINT_TYPE.PRINT_PRODUCT &&
    !!albumCustomPrint.custom_print?.card;

  // TODO - Hack for JKP (3 photo plaque)
  return (photosNo === 3 && description === 'jkp') || isCard;
};

/**
 * Returns the subscription discounted AlbumPrintPackages from an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} excludeDownloadIncludedPackages whether to exclude or not packages with download included
 * @param {Boolean} sellPrintsEnabled If print sales are enabled
 * @returns {*[]}
 */
export const getAlbumMembershipGroupDiscountedAlbumPrintPackages = (
  albumMembershipGroup,
  excludeDownloadIncludedPackages = false,
  sellPrintsEnabled = true,
) => {
  const albumPrintPackages = getAlbumMembershipGroupAlbumPrintPackages(
    albumMembershipGroup,
    excludeDownloadIncludedPackages,
    sellPrintsEnabled,
  );

  return albumPrintPackages.filter(
    albumPrintPackage =>
      !!albumPrintPackage.print_package.discount_price?.value > 0,
  );
};

/**
 * Returns the bundled discounted AlbumPrintPackages from an AlbumMembershipGroup.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup.
 * @param {Boolean} excludeDownloadIncludedPackages whether to exclude or not packages with download included
 * @returns {*[]}
 */
export const getAlbumMembershipGroupBundleDiscountedAlbumPrintPackages = (
  albumMembershipGroup,
  excludeDownloadIncludedPackages = false,
) => {
  const albumPrintPackages = getAlbumMembershipGroupAlbumPrintPackages(
    albumMembershipGroup,
    excludeDownloadIncludedPackages,
  );

  return albumPrintPackages.filter(
    albumPrintPackage =>
      !!albumPrintPackage.print_package.print_package_bundle_discount_price
        ?.value > 0,
  );
};

/**
 * Calculates the savings in price for an AlbumPrintPackage with a bundle discount.
 *
 * @param {Object} albumPrintPackage The AlbumPrintPackage.
 * @returns {number}
 */
export const getAlbumPrintPackageBundleDiscountSavings = albumPrintPackage => {
  if (
    !albumPrintPackage ||
    !albumPrintPackage?.print_package?.print_package_bundle_discount_price
      ?.value
  ) {
    return 0;
  }

  const price = albumPrintPackage.print_package.price?.value;
  const bundleDiscountPrice =
    albumPrintPackage.print_package.print_package_bundle_discount_price?.value;

  return price - bundleDiscountPrice;
};

/**
 * Returns whether an AlbumMembershipGroup has available purchasing options.
 *
 * @param {Object} options The options.
 * @param {Object} options.albumMembershipGroup The AlbumMembershipGroup.
 * @param {Object} [options.identity] The active Identity (optional).
 * @param {Boolean} [options.sellPrintsEnabled] If print sales are enabled
 * @returns {boolean}
 */
export const albumMembershipGroupHasPurchasingOptions = ({
  albumMembershipGroup,
  identity,
  sellPrintsEnabled,
}) => {
  if (!albumMembershipGroup) {
    return false;
  }
  const albumMembershipGroupIdentity = getAlbumMembershipGroupIdentityByIdentityId(
    albumMembershipGroup,
    identity?.id,
  );
  const identityIsPurchased =
    albumMembershipGroupIdentity?.has_purchased_subscription ?? false;

  const subscriptionAlbumFolder = getAlbumMembershipGroupSubscriptionAlbumFolder(
    albumMembershipGroup,
  );
  const subscriptionAlbum = getAlbumMembershipGroupSubscriptionAlbum(
    albumMembershipGroup,
  );

  const hasSubscription = !!subscriptionAlbumFolder || !!subscriptionAlbum;
  // The Identity is optional, if it's available check if it hasn't purchased a sub already, otherwise
  // check if subs are generally available on the AlbumFolder/Album.
  const subscriptionAvailable = identity
    ? hasSubscription && !identityIsPurchased
    : hasSubscription;
  const hasAlbumPrintPackages =
    getAlbumMembershipGroupAlbumPrintPackages(albumMembershipGroup).length > 0;
  const hasPhotoPricing = !!getAlbumMembershipGroupPhotoPricing(
    albumMembershipGroup,
  );
  const hasAlbumCustomPrints =
    getAlbumMembershipGroupAlbumCustomPrints(albumMembershipGroup).length > 0;
  const hasSpecialtyPrints =
    getAlbumMembershipGroupSpecialtyAlbumCustomPrints(albumMembershipGroup)
      .length > 0;
  const supportsLegacyPrints = albumMembershipGroupSupportsLegacyPrintPurchasing(
    albumMembershipGroup,
  );
  const hasDigitalBundles =
    getAlbumMembershipGroupDigitalBundles(albumMembershipGroup).length > 0;

  // If PRO can't sell prints only check these 2
  if (!sellPrintsEnabled) {
    return subscriptionAvailable || hasDigitalBundles;
  }

  return (
    subscriptionAvailable ||
    hasAlbumPrintPackages ||
    hasPhotoPricing ||
    hasAlbumCustomPrints ||
    hasSpecialtyPrints ||
    supportsLegacyPrints ||
    hasDigitalBundles
  );
};

/**
 * Returns whether an AlbumMembershipGroup should display AlbumCustomPrint purchasing options.
 *
 * @param {Object} albumMembershipGroup The AlbumMembershipGroup/
 * @returns {boolean}
 */
export const albumMembershipGroupSupportsAlbumCustomPrintPurchasing = albumMembershipGroup => {
  // As of June 5, 2025, we are not blocking gallery level album Custom Print Purchasing on PPA.
  return !!albumMembershipGroup;
};

/**
 * Determines whether the identity has a subscription in cart.
 *
 * @param {Object} options The options.
 * @param {Array<Object>} options.cart The cart.
 * @param {Object} options.albumFolder The subscription AlbumFolder.
 * @param {Object} options.album The subscription Album.
 * @param {Object} options.identity The Identity.
 * @returns {boolean}
 */
export const hasSubscriptionInCart = ({
  cart,
  albumFolder,
  album,
  identity,
}) => {
  if ((!albumFolder && !album) || !identity) {
    return false;
  }

  const hasAlbumFolderSubscriptionInCart = !!getAlbumFolderSubscriptionCartItem(
    cart,
    {
      albumFolderId: albumFolder?.id,
      identityId: identity.id,
    },
  );
  const hasAlbumSubscriptionInCart = !!getAlbumSubscriptionCartItem(cart, {
    albumId: album?.id,
    identityId: identity?.id,
  });

  return hasAlbumSubscriptionInCart || hasAlbumFolderSubscriptionInCart;
};

/**
 * Retrieve the tagged item for a given albumPrintPackage
 *
 * @param {Object} albumPrintPackage The AlbumPrintPackage.
 * @returns {object}
 */
export const getAlbumPrintPackageTaggedItem = albumPrintPackage => {
  if (!albumPrintPackage) {
    return undefined;
  }

  const printPackage = albumPrintPackage.print_package;

  if (!printPackage) {
    return undefined;
  }

  const printPackageItemWithOverrides = printPackage.items.records.find(
    printPackageItem =>
      printPackageItem.album_folder_print_package_items.total > 0,
  );
  const albumFolderPrintPackageItem = (
    printPackageItemWithOverrides?.album_folder_print_package_items?.records ??
    []
  ).find(
    item =>
      item.album_folder_print_package.album_folder.id ===
      albumPrintPackage.album_folder?.id,
  );

  if (!albumFolderPrintPackageItem) {
    return undefined;
  }

  // Return an overriden version of this item
  return {
    ...printPackageItemWithOverrides,
    ...omit(albumFolderPrintPackageItem, ['id']),
  };
};

/**
 * Retrieve the green screen enabled setting for the albumMembershipGroup
 *
 * @param {Object} albumMembershipGroup the albumMembershipGroup
 * @returns {boolean}
 */
export const getAlbumMembershipGroupGreenScreenStatus = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return false;
  }

  if (albumMembershipGroup?.album) {
    return !!albumMembershipGroup.album?.green_screen_enabled;
  }

  return !!albumMembershipGroup.album_folder?.green_screen_enabled;
};

export const getAlbumMembershipGroupPrintPartnerOrderServices = albumMembershipGroup => {
  if (!albumMembershipGroup) {
    return [];
  }

  const entity = getAlbumMembershipGroupEntity(albumMembershipGroup);
  const albumFolder = isAlbumFolder(entity)
    ? entity
    : entity?.root_album_folder;

  return (
    albumFolder?.print_order_configuration?.print_partner_order_services
      ?.records ?? []
  ).map(({ print_partner_order_service: service }) => service);
};
